/** O termo de entrega: um PDF que identifica cada ficheiro entregue pela
 *  sua impressão digital (SHA-256), para ser assinado com o Cartão de
 *  Cidadão ou a Chave Móvel Digital (Autenticação.gov) ou com qualquer
 *  outra ferramenta de assinatura qualificada em PDF (PAdES).
 *
 *  Porquê assim e não dentro do IFC: o IFC não tem assinatura que os
 *  programas leiam (a secção SIGNATURE da 3.ª edição do STEP não é
 *  suportada pelo Revit nem pelo web-ifc), e qualquer alteração ao
 *  ficheiro mudava a impressão digital. O termo fica ao lado: os IFC
 *  continuam intactos e editáveis, e a assinatura do termo prova quem
 *  entregou o quê.
 *
 *  A lista dos ficheiros vai visível na página e também num bloco de
 *  texto simples (o "manifesto"), entre marcadores, num objecto do PDF
 *  sem compressão. Assinar em PAdES acrescenta ao fim do ficheiro sem
 *  reescrever o que lá estava, por isso o hub volta a encontrar o
 *  manifesto num termo assinado (ver lerEntrega.ts). */

import { PDFDocument, PDFName, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib'

export const VERSAO_DO_MANIFESTO = 1
export const MARCA_INICIO = 'VN2R-ENTREGA-INICIO'
export const MARCA_FIM = 'VN2R-ENTREGA-FIM'

export interface FicheiroEntregue {
  nome: string
  /** Bytes. */
  tamanho: number
  /** Hexadecimal, minúsculas. */
  sha256: string
  /** "IFC 4.3", "IFC 4", ...; vazio quando não se sabe. */
  esquema?: string
}

export interface DadosDoTermo {
  projecto: string
  /** Designação da entrega (fase, revisão, propósito). */
  entrega: string
  emitidoPor: string
  destinatario: string
  /** AAAA-MM-DD. */
  data: string
  observacoes?: string
}

export interface ManifestoDeEntrega extends DadosDoTermo {
  versao: number
  geradoEm: string
  ficheiros: FicheiroEntregue[]
}

export function criarManifesto(dados: DadosDoTermo, ficheiros: FicheiroEntregue[], agora = new Date()): ManifestoDeEntrega {
  return {
    versao: VERSAO_DO_MANIFESTO,
    geradoEm: agora.toISOString(),
    ...dados,
    ficheiros: ficheiros.map((f) => ({ ...f, sha256: f.sha256.toLowerCase() })),
  }
}

/** O manifesto em texto ASCII (o JSON escapa o resto), entre marcadores. */
export function manifestoEmTexto(m: ManifestoDeEntrega): string {
  const json = JSON.stringify(m).replace(/[\u007f-￿]/g, (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`)
  return `${MARCA_INICIO}\n${json}\n${MARCA_FIM}\n`
}

export function tamanhoLegivel(bytes: number): string {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1).replace('.', ',')} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} kB`
  return `${bytes} B`
}

/** O que a fonte base do PDF não sabe escrever passa a "?", em vez de
 *  rebentar a geração por causa de um emoji num nome de ficheiro. */
function seguro(fonte: PDFFont, t: string): string {
  let s = ''
  for (const c of t) {
    try {
      fonte.encodeText(c)
      s += c
    } catch {
      s += '?'
    }
  }
  return s
}

/** Parte um texto em linhas que caibam na largura. */
function partir(fonte: PDFFont, tam: number, t: string, largura: number): string[] {
  const linhas: string[] = []
  for (const paragrafo of t.split('\n')) {
    let linha = ''
    for (const palavra of paragrafo.split(/(\s+)/)) {
      const tentativa = linha + palavra
      if (fonte.widthOfTextAtSize(tentativa, tam) <= largura || linha === '') {
        // Uma palavra sozinha maior do que a linha (um nome de ficheiro
        // comprido) parte-se ao carácter.
        if (fonte.widthOfTextAtSize(tentativa, tam) > largura) {
          let bocado = ''
          for (const c of tentativa) {
            if (fonte.widthOfTextAtSize(bocado + c, tam) > largura) {
              linhas.push(bocado)
              bocado = ''
            }
            bocado += c
          }
          linha = bocado
        } else {
          linha = tentativa
        }
      } else {
        linhas.push(linha.trimEnd())
        linha = palavra.trimStart()
      }
    }
    linhas.push(linha.trimEnd())
  }
  return linhas
}

const A4: [number, number] = [595.28, 841.89]
const MARGEM = 56
const COR_TEXTO = rgb(0.1, 0.1, 0.12)
const COR_SUAVE = rgb(0.38, 0.4, 0.44)
const COR_LINHA = rgb(0.78, 0.8, 0.83)

/** Gera o PDF do termo. */
export async function gerarTermoPdf(m: ManifestoDeEntrega): Promise<Uint8Array> {
  const doc = await PDFDocument.create()
  doc.setTitle(`Termo de entrega: ${m.projecto || 'modelos IFC'}`)
  doc.setSubject('Termo de entrega de ficheiros identificados por SHA-256')
  doc.setProducer('VN2R openBIM Hub')
  doc.setCreator('VN2R openBIM Hub')
  doc.setCreationDate(new Date(m.geradoEm))
  const normal = await doc.embedFont(StandardFonts.Helvetica)
  const negrito = await doc.embedFont(StandardFonts.HelveticaBold)
  const mono = await doc.embedFont(StandardFonts.Courier)
  const largura = A4[0] - MARGEM * 2

  let pagina: PDFPage = doc.addPage(A4)
  let y = A4[1] - MARGEM
  const novaPaginaSePreciso = (altura: number) => {
    if (y - altura < MARGEM + 40) {
      pagina = doc.addPage(A4)
      y = A4[1] - MARGEM
    }
  }
  const escrever = (t: string, o: { fonte?: PDFFont; tam?: number; cor?: ReturnType<typeof rgb>; x?: number; larg?: number; entre?: number } = {}) => {
    const fonte = o.fonte ?? normal
    const tam = o.tam ?? 10
    const x = o.x ?? MARGEM
    for (const l of partir(fonte, tam, seguro(fonte, t), o.larg ?? largura - (x - MARGEM))) {
      novaPaginaSePreciso(tam + 4)
      pagina.drawText(l, { x, y: y - tam, size: tam, font: fonte, color: o.cor ?? COR_TEXTO })
      y -= tam + (o.entre ?? 4)
    }
  }
  const traco = () => {
    novaPaginaSePreciso(10)
    pagina.drawLine({ start: { x: MARGEM, y: y - 4 }, end: { x: A4[0] - MARGEM, y: y - 4 }, thickness: 0.5, color: COR_LINHA })
    y -= 12
  }

  escrever('TERMO DE ENTREGA', { fonte: negrito, tam: 16, entre: 6 })
  escrever('Ficheiros identificados pela impressão digital SHA-256', { tam: 10, cor: COR_SUAVE, entre: 10 })
  traco()

  const campo = (rotulo: string, valor: string) => {
    if (!valor.trim()) return
    const y0 = y
    escrever(rotulo, { fonte: negrito, tam: 9, cor: COR_SUAVE, larg: 120 })
    const yRotulo = y
    y = y0
    escrever(valor, { tam: 10, x: MARGEM + 130 })
    y = Math.min(y, yRotulo)
  }
  campo('Projecto', m.projecto)
  campo('Entrega', m.entrega)
  campo('Emitido por', m.emitidoPor)
  campo('Destinatário', m.destinatario)
  campo('Data', m.data)
  if (m.observacoes?.trim()) campo('Observações', m.observacoes)
  y -= 6
  traco()

  escrever(
    `Declara-se que a entrega acima identificada é constituída ${m.ficheiros.length === 1 ? 'pelo ficheiro' : `pelos ${m.ficheiros.length} ficheiros`} abaixo. ` +
      'Cada ficheiro é identificado pela sua impressão digital SHA-256: qualquer alteração ao ficheiro, ' +
      'por mínima que seja, produz uma impressão digital diferente.',
    { tam: 10, entre: 4 },
  )
  y -= 8

  m.ficheiros.forEach((f, i) => {
    novaPaginaSePreciso(48)
    escrever(`${i + 1}. ${f.nome}`, { fonte: negrito, tam: 10, entre: 3 })
    escrever([tamanhoLegivel(f.tamanho) + ` (${f.tamanho} bytes)`, f.esquema].filter(Boolean).join(' · '), {
      tam: 9,
      cor: COR_SUAVE,
      x: MARGEM + 14,
      entre: 3,
    })
    escrever(`SHA-256: ${f.sha256}`, { fonte: mono, tam: 8.5, x: MARGEM + 14, entre: 8 })
  })

  traco()
  escrever(
    'Para verificar: abrir este termo assinado e os ficheiros recebidos no VN2R openBIM Hub (Ficheiro, Entrega assinada, ' +
      'Verificar). A validade da assinatura e do certificado confirma-se na aplicação Autenticação.gov, no validador ' +
      'validador.autenticacao.gov.pt ou no validador da Comissão Europeia (DSS).',
    { tam: 8.5, cor: COR_SUAVE },
  )
  escrever(`Termo gerado pelo VN2R openBIM Hub em ${m.geradoEm}.`, { tam: 8.5, cor: COR_SUAVE, entre: 18 })

  novaPaginaSePreciso(70)
  escrever('Assinatura', { fonte: negrito, tam: 9, cor: COR_SUAVE })
  pagina.drawRectangle({ x: MARGEM, y: y - 56, width: 260, height: 52, borderColor: COR_LINHA, borderWidth: 0.5 })
  y -= 60

  // O manifesto: texto simples, sem compressão, ligado ao catálogo para
  // sobreviver a quem reescreva o PDF inteiro.
  const fluxo = doc.context.stream(manifestoEmTexto(m), { Type: 'VN2REntrega', Versao: VERSAO_DO_MANIFESTO })
  doc.catalog.set(PDFName.of('VN2REntrega'), doc.context.register(fluxo))

  // Tabelas xref clássicas (sem object streams): o formato que todas as
  // ferramentas de assinatura conhecem.
  return doc.save({ useObjectStreams: false })
}
